const ASSISTANT_HANDOFF_PREFIX = 'scarlet-assistant-handoff:';
const ASSISTANT_HANDOFF_TTL_MS = 15 * 60 * 1000;

type HandoffStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

type StoredAssistantHandoff = {
  prompt: string;
  expiresAt: number;
};

export function routeAssistantLaunch({
  isAuthenticated,
  context,
  navigate,
  launchAuthenticated,
  createGuestHref = createGuestAssistantLoginHref,
}: {
  isAuthenticated: boolean;
  context?: Record<string, unknown>;
  navigate: (href: string) => void;
  launchAuthenticated: (context?: Record<string, unknown>) => void;
  createGuestHref?: (prompt?: string) => string;
}) {
  if (isAuthenticated) {
    launchAuthenticated(context);
    return;
  }

  const prompt = typeof context?.initialPrompt === 'string'
    ? context.initialPrompt
    : undefined;
  navigate(createGuestHref(prompt));
}

export function createGuestAssistantLoginHref(
  prompt?: string,
  options?: {
    storage?: HandoffStorage;
    createId?: () => string;
    now?: number;
  },
) {
  const trimmed = prompt?.trim() ?? '';
  if (!trimmed) {
    return '/login?redirect=%2Fai-assistant';
  }

  const storage = options?.storage ?? window.sessionStorage;
  const handoffId = (options?.createId ?? crypto.randomUUID)();
  const now = options?.now ?? Date.now();
  const handoff: StoredAssistantHandoff = {
    prompt: trimmed,
    expiresAt: now + ASSISTANT_HANDOFF_TTL_MS,
  };

  storage.setItem(`${ASSISTANT_HANDOFF_PREFIX}${handoffId}`, JSON.stringify(handoff));
  const assistantRoute = `/ai-assistant?handoff=${encodeURIComponent(handoffId)}`;

  return `/login?redirect=${encodeURIComponent(assistantRoute)}`;
}

export function consumeGuestAssistantHandoff(
  handoffId: string,
  options?: {
    storage?: HandoffStorage;
    now?: number;
  },
) {
  if (!handoffId) return null;

  const storage = options?.storage ?? window.sessionStorage;
  const storageKey = `${ASSISTANT_HANDOFF_PREFIX}${handoffId}`;
  const stored = storage.getItem(storageKey);
  storage.removeItem(storageKey);
  if (!stored) return null;

  try {
    const handoff = JSON.parse(stored) as Partial<StoredAssistantHandoff>;
    const now = options?.now ?? Date.now();
    if (
      typeof handoff.prompt !== 'string'
      || !handoff.prompt.trim()
      || typeof handoff.expiresAt !== 'number'
      || handoff.expiresAt <= now
    ) {
      return null;
    }
    return handoff.prompt.trim();
  } catch {
    return null;
  }
}
